import React from 'react';
import {useTranslation} from 'react-i18next';
import {ArrowsClockwise, Trash} from '../../icons';
import {KLAVIYO_CHANNEL, SHOPIFY_CHANNEL, TIENDA_NUBE_CHANNEL, VTEX_CHANNEL} from '../../channels/channels';
import {ActionsMenu} from './ActionsMenu';
import {initialsOf} from './platformsText';

const NS = 'agency_v2.platforms';

/**
 * «Volver a autorizar» sólo donde el formulario de reconexión sabe hacerlo: Shopify y Tiendanube
 * por OAuth, VTEX con sus instrucciones. Para Klaviyo y WooCommerce `ReconnectAdAccountForm` dice
 * «no está implementada» (texto fijo): el clásico lo ofrece igual, acá no se promete. Se vuelven a
 * autorizar cargando la API key otra vez, con «Agregar».
 */
const REAUTHORIZABLE_CHANNELS = [SHOPIFY_CHANNEL, TIENDA_NUBE_CHANNEL, VTEX_CHANNEL];

/**
 * Una tienda (o la cuenta de Klaviyo): su nombre y su dirección, a qué cliente está conectada —o
 * «Sin cliente · Conectar a un cliente»—, quién la autorizó y, en el ⋮, Volver a autorizar · Quitar
 * tienda.
 */
export function StoreRow({store, onConnectClient, onReauthorize, onRemove}) {
    const {t} = useTranslation();
    const clients = store.agency_client_names || [];
    // Klaviyo viaja como tienda, pero es una cuenta de email: su ⋮ no puede decir «Quitar tienda».
    const isAccount = store.channel === KLAVIYO_CHANNEL;

    const items = [];
    if (REAUTHORIZABLE_CHANNELS.includes(store.channel)) {
        items.push({key: 'reauthorize', label: t(`${NS}.store.reauthorize`), icon: <ArrowsClockwise size={16} aria-hidden="true"/>, onSelect: onReauthorize});
        items.push({key: 'divider', divider: true});
    }
    items.push({
        key: 'remove',
        label: t(`${NS}.store.${isAccount ? 'remove_account' : 'remove'}`),
        icon: <Trash size={16} aria-hidden="true"/>,
        onSelect: onRemove,
        danger: true,
    });

    return (
        <div className={`apl-store${store.has_access === false ? ' -err' : ''}`} data-testid="apl-store">
            <div className="apl-store__main">
                <span className="apl-store__name">{store.name || store.account_id}</span>
                {store.url && <span className="apl-meta">{store.url}</span>}
            </div>

            <div className="apl-store__client">
                {clients.length > 0 ? (
                    <span className="apl-client">
                        <span className="apl-client__avatar" aria-hidden="true">{initialsOf(clients[0])}</span>
                        <span>{clients.join(', ')}</span>
                    </span>
                ) : (
                    <span className="apl-store__unassigned">
                        <span className="apl-chip -warn">{t(`${NS}.store.no_client`)}</span>
                        <button type="button" className="apl-link" onClick={onConnectClient}>
                            {t(`${NS}.store.connect_client`)}
                        </button>
                        {/* El porqué que el clásico daba en el tooltip de «Sin vincular». */}
                        <span className="apl-meta">{t(`${NS}.store.no_client_hint`)}</span>
                    </span>
                )}
            </div>

            <div className="apl-store__who">
                {store.email && (
                    <span className="apl-meta">
                        {t(`${NS}.store.authorized_by`, {who: store.email, interpolation: {escapeValue: false}})}
                    </span>
                )}
                {store.managed_by_client && <span className="apl-chip -violet">{t(`${NS}.store.managed_by_client`)}</span>}
                {/* La tienda perdió el acceso: el clásico lo marca en su columna «Accesible». */}
                {store.has_access === false && <span className="apl-chip -err">{t('credential_row.disconnected_chip')}</span>}
            </div>

            <ActionsMenu label={t(`${NS}.store.${isAccount ? 'account_actions' : 'actions'}`)} items={items}/>
        </div>
    );
}

export default StoreRow;
